import React, { useEffect, useState } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';

interface DashboardData {
  cabins: { total: number; needsRepair: number };
  packs: { total: number; active: number };
  equipment: { total: number; needsMaintenance: number };
  users: { total: number };
  upcomingStays: Array<{
    id: string;
    packName: string;
    year: number;
    almanacWeek: number;
    status: string;
  }>;
  recentActivity: Array<{
    id: string;
    action: string;
    entityType: string;
    createdAt: string;
    details: Record<string, unknown>;
  }>;
}

function StatCard({ label, value, subValue, color }: {
  label: string;
  value: number | string;
  subValue?: string;
  color: string;
}) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statIndicator, backgroundColor: color }} />
      <div style={styles.statContent}>
        <div style={styles.statValue}>{value}</div>
        <div style={styles.statLabel}>{label}</div>
        {subValue && <div style={styles.statSub}>{subValue}</div>}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    setLoading(true);
    try {
      const res = await apiFetch<DashboardData>('/api/admin/dashboard');
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError(res.error || 'Failed to load dashboard');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <div style={styles.loading}>Loading dashboard...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (!data) return null;

  return (
    <div>
      <h1 style={styles.pageTitle}>Dashboard</h1>
      <p style={styles.pageSubtitle}>Grimsthorpe Lake overview</p>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        <StatCard
          label="Active Packs"
          value={data.packs.active}
          subValue={`${data.packs.total} total`}
          color={COLORS.statusSuccess}
        />
        <StatCard
          label="Total Cabins"
          value={data.cabins.total}
          subValue={data.cabins.needsRepair > 0 ? `${data.cabins.needsRepair} need repair` : 'All in good condition'}
          color={data.cabins.needsRepair > 0 ? COLORS.statusWarning : COLORS.statusSuccess}
        />
        <StatCard
          label="Equipment"
          value={data.equipment.total}
          subValue={data.equipment.needsMaintenance > 0 ? `${data.equipment.needsMaintenance} need maintenance` : 'All operational'}
          color={data.equipment.needsMaintenance > 0 ? COLORS.statusWarning : COLORS.statusSuccess}
        />
        <StatCard
          label="Users"
          value={data.users.total}
          color={COLORS.statusInfo}
        />
      </div>

      {/* Two column layout: Upcoming Stays + Recent Activity */}
      <div style={styles.twoCol}>
        {/* Upcoming Stays */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Upcoming Stays</h2>
          {data.upcomingStays.length === 0 ? (
            <p style={styles.empty}>No upcoming stays</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Pack</th>
                  <th>Year</th>
                  <th>Week</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.upcomingStays.map((stay) => (
                  <tr key={stay.id}>
                    <td style={{ color: COLORS.textPrimary }}>{stay.packName}</td>
                    <td>{stay.year}</td>
                    <td>{stay.almanacWeek}</td>
                    <td>
                      <span className={`badge badge--${getStatusBadge(stay.status)}`}>
                        {stay.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Recent Activity */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Recent Activity</h2>
          {data.recentActivity.length === 0 ? (
            <p style={styles.empty}>No recent activity</p>
          ) : (
            <div style={styles.activityList}>
              {data.recentActivity.map((log) => (
                <div key={log.id} style={styles.activityItem}>
                  <div style={styles.activityAction}>
                    <span style={{ color: COLORS.brassLight }}>{log.action}</span>
                    <span style={{ color: COLORS.textMuted }}> on </span>
                    <span style={{ color: COLORS.textSecondary }}>{log.entityType}</span>
                  </div>
                  <div style={styles.activityTime}>
                    {log.createdAt ? new Date(log.createdAt).toLocaleString() : '--'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getStatusBadge(status: string): string {
  switch (status) {
    case 'planned': return 'info';
    case 'confirmed': return 'success';
    case 'ongoing': return 'warning';
    case 'completed': return 'muted';
    default: return 'muted';
  }
}

const styles: Record<string, React.CSSProperties> = {
  pageTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: COLORS.textPrimary,
    margin: 0,
  },
  pageSubtitle: {
    fontSize: 14,
    color: COLORS.textMuted,
    marginTop: 4,
    marginBottom: 32,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 16,
    marginBottom: 32,
  },
  statCard: {
    display: 'flex',
    alignItems: 'stretch',
    backgroundColor: COLORS.backgroundSecondary,
    border: `1px solid ${COLORS.borderDefault}`,
    borderRadius: 12,
    overflow: 'hidden',
  },
  statIndicator: {
    width: 4,
    flexShrink: 0,
  },
  statContent: {
    padding: '16px 20px',
  },
  statValue: {
    fontSize: 32,
    fontWeight: 700,
    color: COLORS.textPrimary,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textMuted,
    marginTop: 6,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  statSub: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 4,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
  },
  section: {
    backgroundColor: COLORS.backgroundSecondary,
    border: `1px solid ${COLORS.borderDefault}`,
    borderRadius: 12,
    padding: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 16,
  },
  empty: {
    color: COLORS.textMuted,
    fontSize: 13,
    fontStyle: 'italic',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  activityItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${COLORS.borderDefault}`,
  },
  activityAction: {
    fontSize: 13,
  },
  activityTime: {
    fontSize: 11,
    color: COLORS.textMuted,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
    color: COLORS.textMuted,
    fontSize: 14,
  },
  error: {
    padding: 20,
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    border: '1px solid rgba(220, 53, 69, 0.3)',
    borderRadius: 8,
    color: COLORS.statusError,
    fontSize: 14,
  },
};
